import { useMemo } from "react";
import { Avatar, Style } from "@dicebear/core";
import glyphsDefinition from "@dicebear/styles/glyphs.json";
import { cn } from "~/lib/utils";
import { AGENT_BRAND_COLORS } from "~/services/agents";

const glyphsStyle = new Style(glyphsDefinition);

/** Same id-preferred-over-name fallback used by {@link AgentAvatar} and {@link AgentCard}. */
export function agentAvatarSeed(id?: string | null, name?: string | null): string {
  return id || name || "";
}

export interface AgentAvatarResult {
  /** `data:image/svg+xml` URI for the rendered avatar. */
  dataUri: string;
  /** The glyph color Dicebear resolved for this seed. */
  color: string;
}

/** Deterministically renders the same avatar {@link AgentCard} reads its accent color from. */
export function generateAgentAvatar(seed: string): AgentAvatarResult {
  const avatar = new Avatar(glyphsStyle, { seed, glyphColor: AGENT_BRAND_COLORS });
  const { options } = avatar.toJSON();
  const resolvedColor = options.glyphColor;
  return {
    dataUri: avatar.toDataUri(),
    color: Array.isArray(resolvedColor) ? resolvedColor[0] : resolvedColor,
  };
}

interface AgentAvatarProps {
  /** Agent id — preferred seed. */
  id?: string | null;
  /** Agent name — fallback seed when id is missing. */
  name?: string | null;
  /** Pixel size of the rendered square. */
  size?: number;
  className?: string;
  "aria-hidden"?: boolean | "true" | "false";
  title?: string;
}

export const AgentAvatar = ({
  id,
  name,
  size = 44,
  className,
  title,
  "aria-hidden": ariaHidden,
}: AgentAvatarProps) => {
  const seed = agentAvatarSeed(id, name);
  const { dataUri } = useMemo(() => generateAgentAvatar(seed), [seed]);

  return (
    <span
      className={cn(
        "inline-flex shrink-0 items-center justify-center overflow-hidden rounded-full",
        className
      )}
      style={{ width: size, height: size }}
      aria-hidden={ariaHidden}
      title={title}
    >
      <img src={dataUri} alt="" width={size} height={size} />
    </span>
  );
};
